import { formatNumber } from '@shared/format';
import type { BadgeKind } from '@shared/ui/StateBadge';

/**
 * The vocabularies this area renders, and the two unit conversions it performs.
 *
 * **No vocabulary member is a decision taken here.** The subscription states are
 * the owning service's `CHECK` constraint, the sources are the members
 * `ENTITLEMENT_MODES` currently permits, and both resolvers **fall through to
 * the literal value with the neutral badge** rather than dropping a row or
 * crashing: the badge taxonomy is closed while a configured vocabulary is not,
 * and a sixth state must degrade.
 *
 * The labels are this console's — the wire carries a machine token and every
 * shaping decision stays on the reading side of the hop.
 */

// ---- subscription lifecycle ------------------------------------------------

/**
 * The four states of the owning service's lifecycle, mapped onto the closed
 * badge taxonomy.
 *
 * `ACTIVE` takes success, the kind the design system assigns to «published,
 * active, paid». `EXPIRED` and `CANCELLED` take **neutral, not danger**: red is
 * held for destruction and failure, and a subscription that ran its course is a
 * fact about the calendar rather than a fault. They are told apart by their
 * **label**, which is the one place two neutral states may differ.
 *
 * **There is no `PAST_DUE` and no «ينتهي قريباً».** The state existed for a
 * declined renewal charge being retried behind a grace window, and nothing
 * renews: a period is bought once and ends on its own day. Nor is «expiring
 * soon» computed here in its place — deriving it from a clock would compute an
 * authoritative value on the client, and it is the subscriber's own expiry
 * notice, not this table, that says a period is running out.
 *
 * An unknown state still renders, labelled by its own wire value: a service that
 * grows a fifth state must not blank a column in this console before the console
 * catches up.
 */
const SUBSCRIPTION_STATES: Readonly<Record<string, { label: string; kind: BadgeKind }>> = {
  ACTIVE: { label: 'نشط', kind: 'success' },
  PENDING: { label: 'بانتظار التأكيد', kind: 'neutral' },
  EXPIRED: { label: 'منتهٍ', kind: 'neutral' },
  CANCELLED: { label: 'ملغى', kind: 'neutral' },
};

export function resolveSubscriptionState(state: string): { label: string; kind: BadgeKind } {
  return SUBSCRIPTION_STATES[state] ?? { label: state, kind: 'neutral' };
}

// ---- entitlement sources ---------------------------------------------------

/**
 * The members `ENTITLEMENT_MODES` permits today. `free` and `one_off_purchase`
 * are absent from the setting, so no label exists for them — and if one arrives,
 * it renders as itself rather than as a source this console renamed.
 */
const ENTITLEMENT_SOURCES: Readonly<Record<string, string>> = {
  SUBSCRIPTION: 'اشتراك',
  MANUAL_GRANT: 'منحة يدوية',
};

export function resolveEntitlementSource(source: string): string {
  return ENTITLEMENT_SOURCES[source] ?? source;
}

// ---- units -----------------------------------------------------------------

/**
 * The period as Arabic reads it, not as a template fills it.
 *
 * The unit is months — the catalog names «period» without declaring one, and
 * months is what the subscriber surface already renders, so the operator's input
 * and the subscriber's reading stay in one measure. Arabic counts one, two, and
 * three-to-ten differently from eleven-up, and a single «{n} شهراً» string would
 * be wrong on every plan but the long ones.
 */
export function formatPeriodMonths(months: number): string {
  if (months === 1) return 'شهر واحد';
  if (months === 2) return 'شهران';
  if (months >= 3 && months <= 10) return `${formatNumber(months)} أشهر`;
  return `${formatNumber(months)} شهراً`;
}

/**
 * The fee, from basis points to the percentage a provider quotes.
 *
 * Basis points are what the row stores — the unit that survives without a second
 * field for a fixed component — and a percentage is what the operator contracts
 * in. The figure goes through the shared formatter so the digits are Arabic-Indic
 * like every other human figure; the `٪` sign is appended rather than produced by
 * a percent formatter, which would round `2.5` to a whole number.
 */
export function formatFeeFromBasisPoints(feeBps: number): string {
  return `${formatNumber(feeBps / 100)}٪`;
}

export function feePercentToBasisPoints(percent: number): number {
  return Math.round(percent * 100);
}

export function feeBasisPointsToPercent(feeBps: number): number {
  return feeBps / 100;
}

/**
 * The endpoint the provider posts to, composed for display from the two halves
 * the row stores. The column renders one address because that is the one string
 * the operator also holds in the provider's own console, and the two have to
 * match character for character or the result never arrives.
 */
export function composeCallbackEndpoint(baseUrl: string, callbackPath: string): string {
  const origin = baseUrl.replace(/\/+$/, '');
  const path = callbackPath.startsWith('/') ? callbackPath : `/${callbackPath}`;
  return `${origin}${path}`;
}

/**
 * The inverse, at the request boundary. The split is mechanical — origin against
 * everything after it — rather than semantic, so nothing is invented on the way
 * back to a wire that stores the halves separately.
 *
 * Returns `null` on a string the URL parser refuses, which is what the form's
 * own validation reports rather than a request built from a guess.
 */
export function splitCallbackEndpoint(endpoint: string): { baseUrl: string; callbackPath: string } | null {
  try {
    const url = new URL(endpoint);
    return { baseUrl: url.origin, callbackPath: `${url.pathname}${url.search}` };
  } catch {
    return null;
  }
}
